import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { routeResourceMap, hasRoleAccess } from '../config/routeResourceMap';
import { Box, CircularProgress } from '@mui/material';

interface RoleProtectedRouteProps {
  children: ReactNode;
  path: string;
}

/**
 * Route wrapper that checks if the current user has permission to access the route
 * Uses database-driven RBAC with fallback to role-based access
 * Redirects to dashboard if access is denied
 */
function RoleProtectedRoute({ children, path }: RoleProtectedRouteProps) {
  const { user } = useAuth();
  const { canAccess, isLoading } = usePermissions();
  
  // If no user, the parent ProtectedRoute will handle redirect to login
  if (!user) {
    return <>{children}</>;
  }

  // Show loading while checking permissions
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  // Check database-driven permissions first
  const routeMapping = routeResourceMap[path];
  let hasAccess = false;

  if (routeMapping) {
    // Use database permissions
    hasAccess = canAccess(routeMapping.resource, routeMapping.action);
  }

  // Fallback to role-based access if no permission found
  // This allows super_admin to access everything even if permissions aren't set up
  if (!hasAccess && user.role) {
    hasAccess = hasRoleAccess(user.role, path);
  }
  
  if (!hasAccess) {
    console.warn(`Access denied: User cannot access '${path}'`);
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

export default RoleProtectedRoute;
